import dayjs from 'dayjs';
import { datafluxRum } from '@cloudcare/browser-rum';
import { useLoginStore } from '../hooks/useLoginStore';
import { usePlatformStore } from '../hooks/usePlatformStore';
import { useInvitesStore } from '../hooks/useInvitesStore';
import { PlatformItems } from '@/interface/common';

const orderIdRegex =
  /\/campaign\/(home|invites|deals|detail|delivery|wallet|share|draft\/detail)(?:\/detail)?(?:\/([a-f0-9]{24}|demo_[^\/]+))?/;

export const isMobile = () => {
  if (window.navigator) {
    const ua = navigator.userAgent.toLowerCase();
    return /mobile|android|iphone|ipod|phone|ipad/i.test(ua);
  } else {
    return false;
  }
};

const hasAppFlowParam = () => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('from') === 'app';
  } catch {
    return false;
  }
};

/** 是否运行在真实 Capacitor App WebView 中，而不是带了 from=app 的系统浏览器页。 */
export const isNativeAppRuntime = () => {
  if (typeof window === 'undefined') return false;
  try {
    const capacitor = (window as any)?.Capacitor;
    if (!capacitor) return false;
    if (typeof capacitor.isNativePlatform === 'function') {
      return !!capacitor.isNativePlatform();
    }
    return !!capacitor.Plugins;
  } catch {
    return false;
  }
};

/** 是否从 App 内嵌 WebView 打开（用于刘海屏安全区等适配） */
export const isFromApp = () => {
  if (typeof document === 'undefined') return false;
  if (hasAppFlowParam() || isNativeAppRuntime()) {
    document.documentElement.setAttribute('data-from-app', 'true');
    return true;
  }
  return document.documentElement.getAttribute('data-from-app') === 'true';
};

let loginRedirectSuppressedUntil = 0;

export const suppressLoginRedirect = (durationMs = 5000) => {
  if (typeof window === 'undefined') return;
  loginRedirectSuppressedUntil = Date.now() + durationMs;
};

export const isLoginRedirectSuppressed = () => {
  if (typeof window === 'undefined') return false;
  return Date.now() < loginRedirectSuppressedUntil;
};

export const isWebPage = () => {
  const pathname = window.location.pathname;
  return pathname.startsWith('/web/campaign/') || pathname.startsWith('/web');
};

export const isWebOnPc = () => {
  return !isMobile() && isWebPage();
};

export const getOrderIdFromUrl = () => {
  try {
    // orderIdFromUrlSearch 访问链接
    // /campaign/home/xxx
    // /campaign/detail/xxx
    // /campaign/delivery/xxx
    // /draft/detail/xxx
    const pathname = window.location.pathname;
    const regArray = pathname.match(orderIdRegex);
    return regArray?.[2] || '';
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getOrderIdFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('orderId');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getRouterFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('router') || 'today';
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getOrderIdFromGoogleState = () => {
  try {
    const query = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const state = query.get('state') || '';
    let orderId;
    if (state) {
      try {
        const payload = JSON.parse(state);
        orderId = payload.orderId;
      } catch (e) {
        console.error('failed to parse state', e);
      }
    }
    return orderId;
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getOrderId = () => {
  const orderId =
    getOrderIdFromUrl() ||
    getOrderIdFromUrlSearch() ||
    getOrderIdFromGoogleState() ||
    '';
  try {
    if (!orderId) {
      const error = new Error(
        `MOBILE: No orderid!!!, homepage is ${window.localStorage.getItem('homepage')}`
      );
      datafluxRum && datafluxRum.addError(error);
    }
  } catch (e) {
    console.log(e);
  }

  return orderId;
};

const getGoogleState = (type: 'joinCampaign' | 'orderId') => {
  try {
    const query = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const state = query.get('state') || '';
    let payload;
    let result;
    if (state) {
      try {
        payload = JSON.parse(state);
        result = payload[type];
      } catch (e) {
        console.error('failed to parse state', e);
      }
    }
    return result;
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getJoinCampaign = () => {
  // 获取 ?joinCampaign=xxx 和 ?=xxx&joinCampaign=xxx 的情况
  // and 获取 google 返回的 joinCampaign 参数
  const joinCampaignFromGoogleState = getGoogleState('joinCampaign');
  if (
    joinCampaignFromGoogleState === true ||
    joinCampaignFromGoogleState === 'true'
  ) {
    return true;
  }

  try {
    const search = window.location.search;
    const joinCampaign = new URLSearchParams(search)?.get('joinCampaign');
    if (joinCampaign === 'true') {
      return true;
    }
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getAhaSourceFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('aha_source');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getAuthorizeError = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('AuthorizeError');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getUpdateTypeFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('updateType');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getDebugger = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('debugger');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getUpdateReasonFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('update_reason');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getAuthorizeErrorMessage = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('AuthorizeErrorMessage');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getShowModalAnalysisFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('urlShowModalAnalysis');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getShowModalConfirmDealPriceFromUrlSearch = () => {
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('urlShowModalConfirmDealPrice');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getConnectErrorPageUrl = (params?: any) => {
  const { platform, type, message, payload } = params;
  let url = `/auth/${platform}/connectError?type=${type}`;
  if (params?.orderId) {
    url += `&orderId=${params?.orderId}`;
  }
  if (message) {
    url += `&message=${message}`;
  }
  if (payload) {
    url += `&payload=${payload ? encodeURIComponent(JSON.stringify(payload)) : ''}`;
  }
  return url;
};

export const getLoginPageUrl = (params?: any) => {
  const orderId = getOrderId();
  // 需要优化链接拼接参数。
  let loginPageUrl = `/campaign/login`;
  if (orderId) {
    if (params?.joinCampaign) {
      loginPageUrl = `/campaign/login?orderId=${orderId}&joinCampaign=true`;
    } else {
      loginPageUrl = `/campaign/login?orderId=${orderId}`;
    }
  }
  return loginPageUrl;
};

export const getAuthPageUrl = () => {
  const orderId = getOrderId();

  let authPageUrl = `/auth`;
  if (orderId) {
    authPageUrl = `/auth?orderId=${orderId}`;
  }
  return authPageUrl;
};

export const getHomePageUrl = () => {
  const orderId = getOrderId();
  return `/campaign/home/${orderId}`;
};

export const getDetailPageUrl = (params?: any) => {
  const orderId = params?.orderId || getOrderId();
  if (params?.urlShowModalAnalysis) {
    if (params?.AuthorizeError) {
      return `/campaign/detail/${orderId}?urlShowModalAnalysis=true&AuthorizeError=${params?.AuthorizeError}&AuthorizeErrorMessage=${params?.AuthorizeErrorMessage}&updateType=${params?.updateType}&updateReason=${params?.updateReason}`;
    }
    return `/campaign/detail/${orderId}?urlShowModalAnalysis=true`;
  }
  if (params?.AuthorizeError) {
    return `/campaign/detail/${orderId}?AuthorizeError=${params?.AuthorizeError}&AuthorizeErrorMessage=${params?.AuthorizeErrorMessage}&updateType=${params?.updateType}&updateReason=${params?.updateReason}`;
  }
  if (params?.joinCampaign && params?.loginSuccess) {
    return `/campaign/detail/${orderId}?joinCampaign=true&loginSuccess=true`;
  }
  if (params?.joinCampaign) {
    return `/campaign/detail/${orderId}?joinCampaign=true`;
  }
  if (params?.loginSuccess) {
    return `/campaign/detail/${orderId}?loginSuccess=true`;
  }
  if (params?.showLoginModal) {
    return `/campaign/detail/${orderId}?showLoginModal=true`;
  }
  return `/campaign/detail/${orderId}`;
};

export const appendUrlParams = (
  baseUrl: string,
  params: Record<string, any>
) => {
  try {
    const url = new URL(baseUrl, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value);
      }
    });
    return url.pathname + url.search;
  } catch (error) {
    console.error('Failed to append URL params:', error);
    return baseUrl;
  }
};

export const getHomeUrl = (params?: any) => {
  const urlParams: any = {};
  if (params?.showLoginModal) {
    urlParams.showLoginModal = 'true';
  }
  const REFERRALCODE = getKeyFromUrlSearch('REFERRALCODE');
  if (REFERRALCODE) {
    urlParams.REFERRALCODE = REFERRALCODE;
  }

  return appendUrlParams('/', urlParams);
};

export const getJoinCampaignFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('joinCampaign');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getNoQuotaFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('noQuota');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getLoginSuccessFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('loginSuccess');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getShowLoginModalFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('showLoginModal');
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const getAppliedFromUrlSearch = () => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get('applied');
  } catch (error) {
    console.error(error);
    return '';
  }
};

/**
 * 收集并存储 UTM 参数到 localStorage，按 orderId 区分存储
 * @param orderId - 订单 ID
 * 示例 URL: /campaign/detail/123?utm_source=ig&utm_medium=dm&utm_campaign=invitation_campaign
 */
export const collectUtmParams = (orderId: string) => {
  try {
    if (!orderId) {
      console.warn('No orderId provided, UTM params not saved');
      return;
    }

    const search = window.location.search;
    if (!search) return;

    const urlParams = new URLSearchParams(search);
    const utmParams: Record<string, string> = {};

    // 收集所有 UTM 参数
    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content'
    ];

    let hasUtmParams = false;
    utmKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        utmParams[key] = value;
        hasUtmParams = true;
      }
    });

    // 如果有 UTM 参数，按 orderId 存储到 localStorage
    if (hasUtmParams) {
      // 读取现有的 UTM 参数映射
      const storedData = window.localStorage.getItem('utmParams');
      const allUtmParams = storedData ? JSON.parse(storedData) : {};

      // 按 orderId 存储
      allUtmParams[orderId] = utmParams;
      window.localStorage.setItem('utmParams', JSON.stringify(allUtmParams));

      console.log(`UTM params collected for order ${orderId}:`, utmParams);
    }
  } catch (error) {
    console.error('Error collecting UTM params:', error);
  }
};

/**
 * 获取指定订单的 Order Source，从 localStorage 的 UTM 参数中读取 utm_medium
 * @param orderId - 订单 ID
 * @returns 'dm' | 'email' | 'default'
 */
export const getOrderSource = (orderId: string): 'dm' | 'email' | 'default' => {
  try {
    const stored = window.localStorage.getItem('utmParams');
    if (!stored) return 'default';

    const allUtmParams = JSON.parse(stored);
    const utmParams = allUtmParams[orderId];

    if (!utmParams) return 'default';

    const medium = utmParams.utm_medium;
    if (!medium) return 'default';

    const normalizedMedium = medium.toLowerCase();
    if (normalizedMedium === 'dm') return 'dm';
    if (normalizedMedium === 'email') return 'email';

    return 'default';
  } catch (error) {
    console.error('Error getting order source:', error);
    return 'default';
  }
};

export const getKeyFromUrlSearch = (key: string) => {
  // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
  try {
    const search = window.location.search;
    return new URLSearchParams(search)?.get(key);
  } catch (error) {
    console.error(error);
    return '';
  }
};

/** 邀请码在 localStorage 的 key，只认用户首次携带的 REFERRALCODE */
export const REFERRALCODE_STORAGE_KEY = 'aha_referral_code';
export const LEADERBOARD_SHARE_ID_STORAGE_KEY = 'aha_leaderboard_share_id';

/** 读取已存储的邀请码（仅首次访问时从 URL 写入的） */
export const getStoredReferralCode = (): string | null => {
  try {
    return window.localStorage.getItem(REFERRALCODE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** 若当前没有存储过邀请码，则写入；已有则忽略（后续 URL 带不同值也不覆盖） */
export const setStoredReferralCodeIfEmpty = (code: string): void => {
  if (!code || typeof code !== 'string' || !code.trim()) return;
  try {
    const key = REFERRALCODE_STORAGE_KEY;
    if (!window.localStorage.getItem(key)) {
      window.localStorage.setItem(key, code.trim());
    }
  } catch {
    // ignore
  }
};

/** 邀请码上报成功后清除，避免后续登录或注册流程误用旧值 */
export const clearStoredReferralCode = (): void => {
  try {
    window.localStorage.removeItem(REFERRALCODE_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const getStoredLeaderboardShareId = (): string | null => {
  try {
    return window.localStorage.getItem(LEADERBOARD_SHARE_ID_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredLeaderboardShareId = (shareId: string): void => {
  if (!shareId || typeof shareId !== 'string' || !shareId.trim()) return;
  try {
    window.localStorage.setItem(
      LEADERBOARD_SHARE_ID_STORAGE_KEY,
      shareId.trim()
    );
  } catch {
    // ignore
  }
};

export const clearStoredLeaderboardShareId = (): void => {
  try {
    window.localStorage.removeItem(LEADERBOARD_SHARE_ID_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** 注册页邀请码输入框的 sessionStorage key，用于 OAuth 回调后仍能拿到用户实际输入的值 */
const SIGNUP_REFERRAL_INPUT_KEY = 'aha_signup_referral_code_input';

/** 写入注册页用户输入的邀请码（仅本次注册流程有效，OAuth 回调后全局上报任务会优先使用此值） */
export const setSignupReferralCodeInput = (code: string): void => {
  try {
    if (code != null && typeof code === 'string') {
      window.sessionStorage.setItem(SIGNUP_REFERRAL_INPUT_KEY, code.trim());
    }
  } catch {
    // ignore
  }
};

/** 读取注册页用户输入的邀请码，无则返回 null（全局上报任务会回退到 URL/cache） */
export const getSignupReferralCodeInput = (): string | null => {
  try {
    return window.sessionStorage.getItem(SIGNUP_REFERRAL_INPUT_KEY);
  } catch {
    return null;
  }
};

/** 上报成功后清除，避免重复使用 */
export const clearSignupReferralCodeInput = (): void => {
  try {
    window.sessionStorage.removeItem(SIGNUP_REFERRAL_INPUT_KEY);
  } catch {
    // ignore
  }
};

export const getQrCodeLink = () => {
  const origin = window.location.origin;
  const router = getRouterFromUrlSearch();
  const orderId = getOrderIdFromUrlSearch();
  if (router && orderId) {
    return `${origin}/campaign/${router}/${orderId}`;
  }
  return '';
};

export const getOrderIdAndRouterFromUrl = () => {
  const pathname = window.location.pathname;
  const regArray = pathname.match(orderIdRegex);
  const router = regArray?.[1] === 'detail' ? 'detail' : 'home';
  const orderId = getOrderId();
  return {
    router,
    orderId
  };
};

export const getRouterFromUrl = (): 'home' | 'detail' => {
  // orderIdFromUrl 直接访问 /home /detail /delivery 可取的
  const pathname = window.location.pathname;
  const regArray = pathname.match(orderIdRegex);
  const router = regArray?.[1] === 'detail' ? 'detail' : 'home';
  return router as 'home' | 'detail';
};

export const getRealRouterFromUrl = ():
  | 'today'
  | 'invites'
  | 'deals'
  | 'detail'
  | 'delivery'
  | 'wallet'
  | 'share'
  | 'draft/detail'
  | '' => {
  const pathname = window.location.pathname;
  const regArray = pathname.match(orderIdRegex);
  const router = regArray?.[1] || 'today';
  return router as
    | 'today'
    | 'invites'
    | 'deals'
    | 'detail'
    | 'delivery'
    | 'wallet'
    | 'share'
    | 'draft/detail'
    | '';
};

export const getToken = () => {
  return window.localStorage.getItem('token');
};

export const getClerkSignupRedirectUrlPayload = (detailData?: any) => {
  return {
    orderId: detailData?.order?._id,
    influencerId: detailData?.influencer?.influencerId,
    influencerUserEmail: detailData?.influencer?.user?.email
  };
};

export const getClerkSignInRedirectUrl = (params?: any) => {
  const orderId = params?.payload?.orderId || getOrderId();
  const REFERRALCODE =
    params?.payload?.REFERRALCODE ?? getKeyFromUrlSearch('REFERRALCODE');
  const leaderboardShareId =
    params?.payload?.leaderboardShareId ??
    getKeyFromUrlSearch('leaderboardShareId') ??
    getStoredLeaderboardShareId();
  const utmSource =
    params?.payload?.utm_source ??
    getKeyFromUrlSearch('utm_source') ??
    window.localStorage.getItem('utm_source');
  const utmMedium =
    params?.payload?.utm_medium ??
    getKeyFromUrlSearch('utm_medium') ??
    window.localStorage.getItem('utm_medium');
  const utmCampaign =
    params?.payload?.utm_campaign ??
    getKeyFromUrlSearch('utm_campaign') ??
    window.localStorage.getItem('utm_campaign');
  //
  const urlParams: any = {
    orderId: orderId,
    campaignId: params?.payload?.campaignId,
    platform: params?.payload?.platform,
    duringFirstOrder: params?.payload?.duringFirstOrder,
    contentType: params?.payload?.interestedPriceFields?.[0]?.contentType,
    contentGenre: params?.payload?.interestedPriceFields?.[0]?.contentGenre,
    price: params?.payload?.interestedPriceFields?.[0]?.price,
    influencerId: params?.payload?.influencerId,
    influencerUserEmail: params?.payload?.influencerUserEmail,
    skipScript: params?.payload?.skipScript,
    REFERRALCODE: REFERRALCODE,
    leaderboardShareId,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    isSignup: params?.payload?.isSignup,
    urlShowModalAnalysis: params?.payload?.urlShowModalAnalysis,
    urlShowModalConfirmDealPrice: params?.payload?.urlShowModalConfirmDealPrice
  };

  const baseRedirect = params?.payload?.ahaRedirect ?? window.location.pathname;
  urlParams.ahaRedirect = baseRedirect;
  // App 场景：为 OAuth/Clerk 回调（最终落到系统浏览器）保留“来自 App”的标记，
  // 以便回调页按 isFromApp() 走 app-only 的重定向兜底逻辑。
  if (isFromApp()) {
    urlParams.from = 'app';
  }

  if (orderId) {
    // 有orderId 说明是详情页进入
    if (params?.payload) {
      if (
        !params.payload?.urlShowModalAnalysis &&
        !params.payload?.urlShowModalConfirmDealPrice
      ) {
        urlParams.ahaRedirect = appendUrlParams(baseRedirect, {
          loginSuccess: true
        });
      }
    }
  }
  const result = appendUrlParams('/clerk', urlParams);

  return result;
};

export const getClerkSignupRedirectUrl = (params?: any) => {
  const obj = { ...params, payload: { ...params?.payload, isSignup: true } };
  return getClerkSignInRedirectUrl(obj);
};

/**
 * 登录/注册后的重定向地址：若目标是登录页或注册页则改为首页，避免登完又停在登录/注册页
 */
export const normalizeRedirectAfterAuth = (redirect: string): string => {
  if (!redirect || redirect === '/') return '/';
  const path = redirect.includes('?') ? redirect.split('?')[0] : redirect;
  const p = path.replace(/\/+$/, '') || '/';
  if (
    p === '/login' ||
    p === '/signup' ||
    p.startsWith('/login/') ||
    p.startsWith('/signup/')
  )
    return '/';
  return redirect;
};

export const getAuthSuccessRedirectUrl = ({
  isSignup,
  redirect,
  ahaRedirect,
  orderId,
  urlShowModalAnalysis,
  urlShowModalConfirmDealPrice
}: {
  isSignup?: boolean;
  redirect?: string;
  ahaRedirect?: string;
  orderId?: string | null;
  urlShowModalAnalysis?: boolean;
  urlShowModalConfirmDealPrice?: boolean;
}) => {
  if (isSignup) {
    return '/onboarding';
  }
  const { influencers } = usePlatformStore.getState();
  let base = '/';
  try {
    const target = new URL(
      ahaRedirect || redirect || '/',
      window.location.origin
    );
    if (target.origin === window.location.origin) {
      base = normalizeRedirectAfterAuth(
        target.pathname + target.search + target.hash
      );
    }
  } catch {
    // 无效目标回到首页。
  }
  if (!orderId) {
    const influencerList = Array.isArray(influencers) ? influencers : [];
    if (influencerList.length === 0) {
      return '/onboarding';
    }

    const hasVerifiedInfluencer = influencerList.some(
      (influencer: any) => influencer?.status === 'bound_by_me'
    );
    if (hasVerifiedInfluencer) {
      return base;
    }

    const hasStatusField = influencerList.some(
      (influencer: any) => typeof influencer?.status === 'string'
    );
    if (hasStatusField) {
      return '/onboarding/connect-socials';
    }
    return base;
  }
  const target = new URL(base, window.location.origin);
  target.searchParams.set('orderId', orderId);
  if (urlShowModalAnalysis) {
    target.searchParams.set('urlShowModalAnalysis', 'true');
  } else if (urlShowModalConfirmDealPrice) {
    target.searchParams.set('urlShowModalConfirmDealPrice', 'true');
  }
  return target.pathname + target.search + target.hash;
};

/**
 * 构建跳转到登录页的 URL，用于未登录时跳转（替代 openSignIn 弹窗）
 * @param payload - 可选，与 getClerkSignInRedirectUrl 的 payload 一致，登录页会转发到 clerk 回调
 */
export const buildLoginUrl = (payload?: Record<string, any>) => {
  const raw =
    payload?.ahaRedirect ?? window.location.pathname + window.location.search;
  const ahaRedirect = normalizeRedirectAfterAuth(raw);
  const params: Record<string, any> = { redirect: ahaRedirect };
  if (payload) {
    if (payload.orderId) params.orderId = payload.orderId;
    if (payload.campaignId) params.campaignId = payload.campaignId;
    if (payload.platform) params.platform = payload.platform;
    if (payload.duringFirstOrder !== undefined)
      params.duringFirstOrder = payload.duringFirstOrder;
    if (payload.influencerId) params.influencerId = payload.influencerId;
    if (payload.influencerUserEmail)
      params.influencerUserEmail = payload.influencerUserEmail;
    if (payload.urlShowModalAnalysis !== undefined)
      params.urlShowModalAnalysis = payload.urlShowModalAnalysis;
    if (payload.urlShowModalConfirmDealPrice !== undefined)
      params.urlShowModalConfirmDealPrice =
        payload.urlShowModalConfirmDealPrice;
    if (payload.skipScript !== undefined)
      params.skipScript = payload.skipScript;
  }
  const leaderboardShareId =
    payload?.leaderboardShareId ||
    getKeyFromUrlSearch('leaderboardShareId') ||
    getStoredLeaderboardShareId();
  if (leaderboardShareId) params.leaderboardShareId = leaderboardShareId;
  return appendUrlParams('/login', params);
};

/**
 * 构建跳转到注册页的 URL
 */
export const buildSignupUrl = (payload?: Record<string, any>) => {
  const base = buildLoginUrl(payload);
  return base.replace('/login', '/signup');
};

export const getClerkSignOutRedirectUrl = () => {
  // 退出后：公开页（详情、邀请列表等）留在当前页，否则去登录页
  const path = window.location.pathname;
  const stayOnPage = path.includes('/detail') || path === '/campaign/invites';
  return stayOnPage ? window.location.href : '/login';
};

export const hasClerkUser = () => {
  //@ts-ignore
  const token = window?.Clerk?.session?.user;
  if (token) {
    return true;
  } else {
    return false;
  }
};

export const notMatch = ({
  influencers,
  detailData,
  influencerId
}: {
  influencers?: any;
  detailData?: any;
  influencerId?: any;
}) => {
  //
  const orderInfluencerId =
    detailData?.influencer?.influencerId || influencerId;
  //
  if (detailData) {
    const { user, order } = detailData;
    const orderHasUser = Boolean(order?.user?._id);
    if (orderHasUser && Number(user?.id) !== Number(order?.user?._id)) {
      return true;
    }
  }
  //
  if (!orderInfluencerId) {
    return false;
  }
  //
  if (orderInfluencerId && influencers?.length) {
    const isMatch = influencers.find(
      (el: any) => Number(el.influencerId) === Number(orderInfluencerId)
    );
    return !isMatch;
  }
  return false;
};

export const maskEmail = (email: string): string => {
  if (!email) {
    return '';
  }
  const [username, domain] = email?.split('@');
  let maskedUsername = username;

  if (username.length > 4) {
    const start = username.slice(0, 2);
    const end = username.slice(-2);
    maskedUsername = `${start}***${end}`;
  } else if (username.length > 1) {
    maskedUsername = `${username[0]}***${username[username.length - 1]}`;
  } else {
    maskedUsername = '***';
  }

  return `${maskedUsername}@${domain}`;
};

export const maskCardNumber = (accountNumber: string): [string, string] => {
  if (!accountNumber) {
    return ['', ''];
  }
  const start = accountNumber.slice(0, 4);
  const end = accountNumber.slice(-4);
  return [start, end];
};

export const maskEmailCard = (email: string): [string, string, string] => {
  if (!email) {
    return ['', '', ''];
  }
  const [username, domain] = email?.split('@');
  let maskedUsername = username;
  let usernameLeft = '';
  // usernamemiddle = '...'
  let usernameRight = '';

  if (username.length > 4) {
    const start = username.slice(0, 2);
    const end = username.slice(-2);
    // maskedUsername = `${start}***${end}`;
    usernameLeft = start;
    usernameRight = end;
  } else if (username.length > 1) {
    // maskedUsername = `${username[0]}***${username[username.length - 1]}`;
    usernameLeft = username[0];
    usernameRight = username[username.length - 1];
  } else {
    // 邮箱太短。只展示domain
    usernameLeft = '';
    usernameRight = '';
  }

  return [usernameLeft, usernameRight, domain];
};

export const clearToken = () => {
  useLoginStore.getState().clearLoginInfo();
  useInvitesStore.getState().clearInvitesId();
  window.localStorage.removeItem('token');
  window.localStorage.removeItem('platform');
};

/**
 * 格式化日期时间 YYYY-MM-DD HH:mm:ss
 * @param date 日期时间字符串
 * @returns 格式化后的日期时间字符串
 */
export const formatDateTime_YMD_HMS = (date: string) => {
  const d = dayjs(date);
  return `${d.format('YYYY-MM-DD HH:mm:ss')}`;
};

export const localStorageSaveUtmMediumAndSource = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const utmMedium = searchParams.get('utm_medium');
  const utmSource = searchParams.get('utm_source');
  const utmCampaign = searchParams.get('utm_campaign');
  if (utmMedium) {
    window.localStorage.setItem('utm_medium', utmMedium);
  }
  if (utmSource) {
    window.localStorage.setItem('utm_source', utmSource);
  }
  if (utmCampaign) {
    window.localStorage.setItem('utm_campaign', utmCampaign);
  }
};

export const getStoredOrUrlUtmValue = (
  key: 'utm_source' | 'utm_medium' | 'utm_campaign'
) => {
  const storedValue = window.localStorage.getItem(key);
  if (storedValue) return storedValue;
  const urlValue = new URLSearchParams(window.location.search).get(key);
  if (urlValue) {
    window.localStorage.setItem(key, urlValue);
  }
  return urlValue;
};

export const handleNameToUrl = ({
  influencer,
  platform
}: {
  influencer: any;
  platform: any;
}) => {
  const url = getPlatformProfileUrl(platform, influencer?.username);
  if (url) {
    window.open(url, '_blank');
    return;
  }
};

export const getPlatformProfileUrl = (platform: string, username: string) => {
  switch (platform.toLowerCase()) {
    case PlatformItems.YOUTUBE.toLowerCase():
      return `https://www.youtube.com/${username}`;
    case PlatformItems.TIKTOK.toLowerCase():
      return `https://www.tiktok.com/@${username}`;
    case PlatformItems.INSTAGRAM.toLowerCase():
      return `https://www.instagram.com/${username}`;
    case PlatformItems.TWITTER.toLowerCase():
      return `https://twitter.com/${username}`;
    case PlatformItems.LINKEDIN.toLowerCase():
      // LinkedIn 的 URL 可能是自定义的，所以最好使用完整的 URL
      // 如果是完整 URL 就直接使用，否则拼接默认格式
      return username.startsWith('http')
        ? username
        : `https://www.linkedin.com/in/${username}`;
    default:
      return '';
  }
};

export const reviewTime = '5';

const OAUTH_RETURN_PATH_KEY = 'influencer_oauth_return_path';

/** 跳转 OAuth / Phyllo 前调用，回调成功后 consumeOAuthReturnPath 可回到该页 */
export const setOAuthReturnPathForNextCallback = () => {
  try {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash || ''}`;
    sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, path);
  } catch {
    // ignore quota / private mode
  }
};

/**
 * 读取并清除 session 中的回跳路径；非法或缺失时使用 fallback。
 * 仅允许同源相对路径，避免开放重定向。
 */
export const consumeOAuthReturnPath = (fallback: string): string => {
  try {
    const raw = sessionStorage.getItem(OAUTH_RETURN_PATH_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
    if (!raw || typeof raw !== 'string') return fallback;
    const trimmed = raw.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return fallback;
    if (trimmed.includes('://')) return fallback;
    return trimmed;
  } catch {
    return fallback;
  }
};
